import { useMemo } from 'react';
import { flatMapDeep, uniq, uniqBy } from 'lodash';

type MinimalResponse = {
  testInstanceUser?: {
    gender?: API_JSON;
    ethnicities?: API_JSON[];
    level?: API_JSON;
    referenceLevel?: API_JSON;
    groups?: API_JSON[];
  };
};

export const useResponsesGenderIds = (responses: MinimalResponse[] | undefined): number[] =>
  useMemo(
    () =>
      uniq(
        flatMapDeep(responses, ({ testInstanceUser }) => testInstanceUser?.gender?.id).filter(
          Number.isInteger
        ) as number[]
      ),
    [responses]
  );

export const useResponsesEthnicityIds = (responses: MinimalResponse[] | undefined): number[] =>
  useMemo(
    () =>
      uniq(
        flatMapDeep(
          responses,
          ({ testInstanceUser }) =>
            testInstanceUser?.ethnicities?.map((eth) => eth.id).filter(Number.isInteger) as number[]
        )
      ),
    [responses]
  );

export const useResponsesLevelIds = (responses: MinimalResponse[] | undefined): number[] =>
  useMemo(
    () =>
      uniq(
        responses
          ?.map(({ testInstanceUser }) => testInstanceUser?.level.id)
          ?.filter(Number.isInteger) as number[]
      ),
    [responses]
  );

export const useResponsesReferenceLevelIds = (responses: MinimalResponse[] | undefined): number[] =>
  useMemo(
    () =>
      uniq(
        responses
          ?.map(({ testInstanceUser }) => testInstanceUser?.referenceLevel.id)
          ?.filter(Number.isInteger) as number[]
      ),
    [responses]
  );

export const useResponsesLevelPairs = (
  responses: MinimalResponse[] | undefined
): { level: number; referenceLevel: number }[] =>
  useMemo(
    () =>
      uniqBy(
        responses
          ?.map(({ testInstanceUser }) => ({
            level: testInstanceUser?.level?.id,
            referenceLevel: testInstanceUser?.referenceLevel?.id
          }))
          ?.filter(({ level }) => Number.isInteger(level)) as {
          level: number;
          referenceLevel: number;
        }[],
        ({ level, referenceLevel }) => `${level}-${referenceLevel}`
      ),
    [responses]
  );

export const useResponsesGroupIds = (responses: MinimalResponse[] | undefined): number[] =>
  useMemo(
    () =>
      uniq(
        responses
          ?.flatMap(({ testInstanceUser }) => testInstanceUser?.groups?.map(({ id }) => id))
          ?.filter(Number.isInteger) as number[]
      ),
    [responses]
  );
